import React, { useState, useEffect } from 'react';
import { ChatSession } from '../../types';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ChatBubbleLeftIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface ChatSessionListProps {
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onNewSession: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return d.toLocaleDateString('it-IT', { weekday: 'short' });
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}

const ChatSessionList: React.FC<ChatSessionListProps> = ({
  sessions,
  activeSessionId,
  onSelect,
  onDelete,
  onRename,
  onNewSession,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (deletingId) {
      const timer = setTimeout(() => setDeletingId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [deletingId]);

  const sorted = [...sessions].sort((a, b) => {
    const aDate = a.last_message_at || a.created_at;
    const bDate = b.last_message_at || b.created_at;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  const startEdit = (session: ChatSession) => {
    setEditingId(session.id);
    setEditTitle(session.title || '');
  };

  const saveEdit = () => {
    if (editingId && editTitle.trim()) {
      onRename(editingId, editTitle.trim());
    }
    setEditingId(null);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200">
        <button
          onClick={onNewSession}
          className="flex items-center space-x-2 w-full px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          <span>Nuova conversazione</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {sorted.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">Nessuna conversazione</p>
        )}

        {sorted.map((session) => (
          <div
            key={session.id}
            className={`group flex items-center px-3 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
              session.id === activeSessionId ? 'bg-primary-50' : ''
            }`}
            onClick={() => {
              if (editingId !== session.id && deletingId !== session.id) {
                onSelect(session.id);
              }
            }}
          >
            {editingId === session.id ? (
              <div className="flex-1 flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  autoFocus
                  className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <button onClick={saveEdit} className="p-1 text-green-600 hover:text-green-700">
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <ChatBubbleLeftIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mr-2" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">
                    {session.title || 'Conversazione senza titolo'}
                  </p>
                  <div className="flex items-center space-x-2 text-[10px] text-gray-400">
                    <span>{formatDate(session.last_message_at || session.created_at)}</span>
                    <span>{session.message_count} msg</span>
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); startEdit(session); }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  {deletingId === session.id ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(session.id); setDeletingId(null); }}
                      className="p-1 text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeletingId(session.id); }}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChatSessionList;
